import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { cn } from '../../../../lib';
import { translations } from '../../../../translations';

interface IFormFeedbackSectionProps {
  /**
   * Indicates whether to style the form message as an error or just info tip.
   */
  type: 'error' | 'info';
  /**
   * The tailwind css classes to augment the default class names.
   */
  className?: string;
  /**
   * The main message displayed below the title of the form.
   */
  message: TTextLocalized;
  /**
   * The alternative `message` displayed when the form is loading.
   */
  messageLoadingText?: TTextLocalized;
  /**
   * Additional information to be shown below the main message.
   */
  description?: TTextLocalized;
  /**
   * The alternative `description` displayed when the form is loading.
   */
  descriptionLoadingText?: TTextLocalized;
  /**
   * Whether the form is in loading state/currently submitting input via API or not.
   */
  isFormSubmitting: boolean;
}

/**
 * -----------------------------------------------------------------------------
 * This displays the form feedback at the top that renders the helpful tips and
 * form error messages returned from the server.
 *
 * TODO: P2: Update these feedback styles to fix broken contrast in screens.
 */
export function FormFeedbackSection({
  type,
  className,
  message,
  messageLoadingText,
  description,
  descriptionLoadingText,
  isFormSubmitting,
}: IFormFeedbackSectionProps) {
  let msg = message;
  let desc = description;
  const hasError = type === 'error';

  if (isFormSubmitting) {
    msg = messageLoadingText || translations.formSubmission.verifyingDetails;
    desc = descriptionLoadingText || translations.formSubmission.pleaseWait;
  }

  return (
    <p
      className={cn(
        'pt-2 text-start font-normal transition-all duration-300 ease-in-out',
        hasError ? '!text-danger' : '!text-info-text',
        className
      )}
    >
      {msg ? (
        <span
          className={cn(
            'text-primary-500 dark:text-primary-200 block text-sm',
            hasError ? '!text-danger' : '!text-info-text'
          )}
        >
          <Text text={msg} />
        </span>
      ) : null}
      {desc ? (
        <span
          className={cn(
            'text-text-caption text-xs font-light',
            hasError ? '!text-failure' : '!text-info-text'
          )}
        >
          <Text text={desc} />
        </span>
      ) : null}
    </p>
  );
}
